import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type WheelEvent,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  CONVERSATION_MESSAGE_PAGE_SIZE,
  latestTodoList,
  isThinkingLevel,
  type AgentDefinition,
  type AgentWorkspaceApproval,
  type Model,
  type ModelPreferences,
  type ThinkingLevel,
  type TodoListSnapshot,
  type User,
} from "@lumen/shared";
import { useModelCatalog } from "../../ModelCatalogContext";
import { api } from "../../api";
import {
  applyConversationSnapshot,
  mergeLatestMessages,
} from "./conversationSnapshots";
import {
  type ComposerActivity,
  type ComposerActivitySettings,
  type ComposerOptions,
} from "../../components/composer/Composer";
import { ChatPageView } from "./ChatPageView";
import { useChatActions } from "./ChatPageActions";
import { useChatConversationActions } from "./ChatPageConversationActions";
import { useChatKeyboardShortcuts } from "./ChatPageKeyboardShortcuts";
import { useChatVersionActions } from "./ChatPageVersionActions";
import { useChatPageEffects } from "./ChatPageEffects";
import {
  composerActivities,
  preferredActivityModelId,
  projectComposerDefaults,
  supportsActivity,
  todoListSignature,
} from "./ChatPage.utils";

export type {
  DisplayMessage,
  GenerationStatusDetails,
  DisplayConversation,
  ShareInfo,
  StreamEvent,
} from "./ChatPage.types";
import type { ShareInfo } from "./ChatPage.types";
import type { DisplayMessage, DisplayConversation } from "./ChatPage.types";
type ConversationState = DisplayConversation | null;

export function ChatPage({
  user,
  refreshConversations,
  onAttention,
  onPrepareAttention,
  onToggleSidebar,
}: {
  user: User;
  refreshConversations: () => Promise<void>;
  onAttention?: (kind: "completed" | "approval") => void;
  onPrepareAttention?: () => void;
  onToggleSidebar?: () => void;
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const requestedModelId = searchParams.get("model");
  const draftProjectId = !id ? (searchParams.get("project") ?? null) : null;
  const projectScoped =
    location.pathname.startsWith("/projects/") || Boolean(draftProjectId);
  const { models, loadModels } = useModelCatalog();
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [preferences, setPreferences] = useState<
    ModelPreferences | undefined
  >();
  const [conversation, setConversation] = useState<ConversationState>(null);
  const seenMessages = useRef(new Set<string>());
  useEffect(() => {
    const markSeen = () => {
      if (!conversation || conversation.id !== id || document.hidden) return;
      const leaf = conversation.messages?.find(
        (message) => message.id === conversation.activeLeafMessageId,
      );
      if (leaf?.role !== "assistant" || leaf.generationStatus !== "completed")
        return;
      const key = `${conversation.id}:${leaf.id}`;
      if (seenMessages.current.has(key)) return;
      seenMessages.current.add(key);
      void api(`/conversations/${conversation.id}/seen`, { method: "POST" })
        .then(() => refreshConversations())
        .catch(() => seenMessages.current.delete(key));
    };
    markSeen();
    document.addEventListener("visibilitychange", markSeen);
    window.addEventListener("focus", markSeen);
    return () => {
      document.removeEventListener("visibilitychange", markSeen);
      window.removeEventListener("focus", markSeen);
    };
  }, [conversation, id, refreshConversations]);
  const [draftComposerDefaults, setDraftComposerDefaults] = useState<
    Partial<ComposerOptions> | undefined
  >();
  const [approvals, setApprovals] = useState<AgentWorkspaceApproval[]>([]);
  const [decidingApprovalIds, setDecidingApprovalIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [modelId, setModelId] = useState("");
  const [activity, setActivity] = useState<ComposerActivity>(() => {
    const saved = localStorage.getItem("lumen:active-activity");
    return saved && composerActivities.includes(saved as ComposerActivity)
      ? (saved as ComposerActivity)
      : "ask";
  });
  const activityModelIds = useRef<Partial<Record<ComposerActivity, string>>>(
    {},
  );
  const [activityModelSelection, setActivityModelSelection] = useState<
    Partial<Record<ComposerActivity, string>>
  >(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("lumen:activity-models") ?? "null",
      ) as unknown;
      return saved && typeof saved === "object"
        ? (saved as Partial<Record<ComposerActivity, string>>)
        : {};
    } catch {
      return {};
    }
  });
  const [activitySettings, setActivitySettings] =
    useState<ComposerActivitySettings>(() => {
      try {
        const saved = JSON.parse(
          localStorage.getItem("lumen:activity-settings") ?? "null",
        ) as unknown;
        return saved && typeof saved === "object"
          ? (saved as ComposerActivitySettings)
          : {};
      } catch {
        return {};
      }
    });
  const activitySettingsRef = useRef(activitySettings);
  useEffect(() => {
    activitySettingsRef.current = activitySettings;
  }, [activitySettings]);
  useEffect(() => {
    Object.assign(activityModelIds.current, activityModelSelection);
  }, [activityModelSelection]);
  const [enabledActivities, setEnabledActivities] = useState<
    ComposerActivity[]
  >(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("lumen:enabled-activities") ?? "null",
      ) as unknown;
      return Array.isArray(saved)
        ? [
            "ask",
            ...saved.filter((item): item is ComposerActivity =>
              [
                "image",
                "audio",
                "video",
                "speech",
                "transcription",
                "model3d",
                "sprite",
                "tileset",
              ].includes(item as string),
            ),
          ]
        : ["ask"];
    } catch {
      return ["ask"];
    }
  });
  const [agentId, setAgentId] = useState("");
  const [thinkingLevel, setThinkingLevel] = useState<ThinkingLevel | "">(() => {
    const saved = localStorage.getItem("lumen:active-thinking-level");
    return isThinkingLevel(saved) ? saved : "";
  });
  const [sending, setSending] = useState(false);
  const [compacting, setCompacting] = useState(false);
  const activeRequest = useRef<AbortController | null>(null);
  const generationMessageId = useRef<string | null>(null);
  const lastEscapePress = useRef(0);
  const [loadingConversation, setLoadingConversation] = useState(Boolean(id));
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState("");
  const reconnectGeneration = useCallback(() => {
    window.location.reload();
  }, []);
  const [deleteTarget, setDeleteTarget] = useState<DisplayMessage | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [deleteConversationOpen, setDeleteConversationOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollArea = useRef<HTMLDivElement>(null);
  const scrollContent = useRef<HTMLDivElement>(null);
  const scrollFrame = useRef(0);
  const cachedScrollMessage = useRef<HTMLElement | null>(null);
  const conversationRef = useRef<DisplayConversation | null>(null);
  const loadingOlderRef = useRef(false);
  const suppressOlderLoadRef = useRef(false);
  const initialScrollPending = useRef(false);
  const stickToBottom = useRef(true);
  const todoListRef = useRef<TodoListSnapshot | null>(null);
  const temporary = id === "temporary";
  const [developmentConfigured, setDevelopmentConfigured] = useState(false);
  const [developmentConfiguration, setDevelopmentConfiguration] = useState<{
    conversationId: string;
    workspaceId: string | null;
    repositoryId: string | null;
  } | null>(null);
  const [developmentOpen, setDevelopmentOpen] = useState(false);
  const [usageRevision, setUsageRevision] = useState(0);
  const { loadApprovals } = useChatPageEffects({
    activeRequest,
    agents,
    agentId,
    applyConversationSnapshot,
    mergeLatestMessages,
    cachedScrollMessage,
    composerActivities,
    conversation,
    conversationRef,
    generationMessageId,
    id,
    draftProjectId,
    initialScrollPending,
    loadingConversation,
    loadingOlderRef,
    onAttention,
    requestedModelId,
    scrollArea,
    scrollContent,
    scrollFrame,
    setActivity,
    activityModelIds,
    setActivityModelSelection,
    setActivitySettings,
    setEnabledActivities,
    setAgentId,
    setAgents,
    setApprovals,
    setCompacting,
    setConversation,
    setDraftComposerDefaults,
    setDevelopmentConfiguration,
    setDevelopmentConfigured,
    setDevelopmentOpen,
    setError,
    setLoadingConversation,
    setModelId,
    setThinkingLevel,
    loadModels,
    setPreferences,
    setSending,
    setShowScrollTop,
    setUsageRevision,
    stickToBottom,
    sending,
    temporary,
    user,
    activity,
    thinkingLevel,
  });
  const selectedAgent = agents.find((agent) => agent.id === agentId);
  const activityModels = useMemo(
    () => models.filter((model) => supportsActivity(model, activity)),
    [activity, models],
  );
  // The model shown in the header belongs to the active mode. Ask uses the
  // conversation model (the agent's model still wins for generation); media
  // modes keep their own per-mode selection and fall back to the mode's
  // default when unset.
  const selectedModelId =
    activity === "ask"
      ? modelId
      : (preferredActivityModelId(
          models,
          activity,
          activityModelSelection,
          preferences,
        ) ?? "");
  useEffect(() => {
    if (
      conversation?.surface !== "project" ||
      conversation.modelId ||
      modelId ||
      !models.length
    )
      return;
    const askModels = models.filter((model) => supportsActivity(model, "ask"));
    const preferred =
      preferences?.activityDefaultModelIds?.ask ??
      preferences?.defaultModelId ??
      preferences?.adminDefaultModelId;
    setModelId(
      (preferred && askModels.some((model) => model.id === preferred)
        ? preferred
        : askModels[0]?.id) ?? "",
    );
  }, [conversation, modelId, models, preferences]);
  const selected = models.find(
    (model) =>
      model.id ===
      (activity === "ask"
        ? (selectedAgent?.modelId ?? modelId)
        : selectedModelId),
  );
  const modelLookup = useMemo(() => {
    const lookup = new Map<string, Model>();
    for (const model of models) {
      lookup.set(model.id, model);
      lookup.set(model.remoteId, model);
      lookup.set(model.name, model);
    }
    return lookup;
  }, [models]);
  const agentLookup = useMemo(
    () => new Map(agents.map((agent) => [agent.id, agent])),
    [agents],
  );
  const approvalsById = useMemo(
    () => new Map(approvals.map((approval) => [approval.id, approval])),
    [approvals],
  );
  const todoList = useMemo(() => {
    const next =
      latestTodoList(conversation?.messages ?? []) ??
      conversation?.todoList ??
      null;
    const previous = todoListRef.current;
    if (
      next &&
      previous &&
      todoListSignature(next) === todoListSignature(previous)
    )
      return previous;
    todoListRef.current = next;
    return next;
  }, [conversation?.messages, conversation?.todoList]);
  const updateConversation = useCallback(
    (update: (current: DisplayConversation) => DisplayConversation) =>
      setConversation((current) => (current ? update(current) : current)),
    [],
  );
  const loadOlderMessages = useCallback(async () => {
    const current = conversationRef.current;
    const area = scrollArea.current;
    if (
      !current ||
      !area ||
      !current.hasMoreMessages ||
      !current.nextMessageCursor ||
      loadingOlderRef.current
    )
      return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const previousHeight = area.scrollHeight;
    const previousTop = area.scrollTop;
    const conversationId = current.id;
    try {
      const older = await api<DisplayConversation>(
        `/conversations/${conversationId}?limit=${CONVERSATION_MESSAGE_PAGE_SIZE}&before=${encodeURIComponent(current.nextMessageCursor)}`,
      );
      setConversation((latest) => {
        if (!latest || latest.id !== conversationId) return latest;
        const existingIds = new Set(
          (latest.messages ?? []).map((message) => message.id),
        );
        const prepended = (older.messages ?? []).filter(
          (message) => !existingIds.has(message.id),
        );
        return {
          ...latest,
          messages: [...prepended, ...(latest.messages ?? [])],
          hasMoreMessages: older.hasMoreMessages,
          nextMessageCursor: older.nextMessageCursor,
        };
      });
      requestAnimationFrame(() => {
        const nextArea = scrollArea.current;
        if (nextArea)
          nextArea.scrollTop =
            previousTop + nextArea.scrollHeight - previousHeight;
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      });
    } catch (reason) {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not load earlier messages",
      );
    }
  }, []);
  const processScrollPosition = useCallback(() => {
    const area = scrollArea.current;
    if (!area) return;
    const areaTop = area.getBoundingClientRect().top;
    const cached = cachedScrollMessage.current;
    let currentMessage: HTMLElement | null = null;
    if (cached?.isConnected) {
      const rect = cached.getBoundingClientRect();
      if (rect.top <= areaTop + 1 && rect.bottom > areaTop + 1)
        currentMessage = cached;
    }
    if (!currentMessage) {
      const visibleMessages = Array.from(
        area.querySelectorAll<HTMLElement>("[data-chat-message]"),
      );
      currentMessage =
        visibleMessages.find((message) => {
          const rect = message.getBoundingClientRect();
          return rect.top <= areaTop + 1 && rect.bottom > areaTop + 1;
        }) ??
        visibleMessages.find(
          (message) => message.getBoundingClientRect().bottom > areaTop + 1,
        ) ??
        null;
    }
    cachedScrollMessage.current = currentMessage;
    const currentMessageRect = currentMessage?.getBoundingClientRect();
    setShowScrollTop(
      Boolean(
        currentMessage &&
        currentMessageRect &&
        currentMessageRect.top < areaTop - 160 &&
        currentMessageRect.height > area.clientHeight,
      ),
    );
  }, []);
  const handleScroll = useCallback(() => {
    const area = scrollArea.current;
    if (!area) return;
    const distanceFromBottom =
      area.scrollHeight - area.scrollTop - area.clientHeight;
    if (stickToBottom.current) {
      stickToBottom.current = distanceFromBottom < 48;
    } else if (distanceFromBottom <= 1) {
      stickToBottom.current = true;
    }
    if (!stickToBottom.current) initialScrollPending.current = false;
    if (area.scrollTop < 160) {
      if (suppressOlderLoadRef.current) suppressOlderLoadRef.current = false;
      else void loadOlderMessages();
    }
    if (scrollFrame.current) return;
    scrollFrame.current = requestAnimationFrame(() => {
      scrollFrame.current = 0;
      processScrollPosition();
    });
  }, [loadOlderMessages, processScrollPosition]);
  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    if (event.deltaY < 0) {
      stickToBottom.current = false;
      initialScrollPending.current = false;
    }
  }, []);
  const currentMessageElement = useCallback(() => {
    const area = scrollArea.current;
    if (!area) return null;
    const areaTop = area.getBoundingClientRect().top;
    const messages = area.querySelectorAll<HTMLElement>("[data-chat-message]");
    let current: HTMLElement | null = null;
    for (const message of messages) {
      if (message.getBoundingClientRect().top - areaTop <= 24)
        current = message;
      else break;
    }
    return current ?? messages[0] ?? null;
  }, []);
  const scrollToTop = useCallback(() => {
    const area = scrollArea.current;
    if (!area) return;
    const message = currentMessageElement();
    if (!message) return;
    stickToBottom.current = false;
    initialScrollPending.current = false;
    suppressOlderLoadRef.current = true;
    const top =
      area.scrollTop +
      message.getBoundingClientRect().top -
      area.getBoundingClientRect().top;
    area.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, [currentMessageElement]);
  const selectModel = useCallback(
    async (next: string) => {
      const previous = modelId;
      setModelId(next);
      const current = conversationRef.current;
      if (!current) return;
      const conversationId = current.id;
      setConversation((current) =>
        current?.id === conversationId
          ? { ...current, modelId: next }
          : current,
      );
      try {
        await api<DisplayConversation>(`/conversations/${conversationId}`, {
          method: "PATCH",
          body: JSON.stringify({ modelId: next }),
        });
      } catch (reason) {
        setModelId(previous);
        setConversation((current) =>
          current?.id === conversationId
            ? { ...current, modelId: previous }
            : current,
        );
        setError(
          reason instanceof Error ? reason.message : "Could not change model",
        );
      }
    },
    [modelId],
  );
  const rememberActivityModel = useCallback(
    (target: ComposerActivity, modelIdValue: string | null) => {
      activityModelIds.current[target] = modelIdValue ?? undefined;
      setActivityModelSelection((current) => ({
        ...current,
        [target]: modelIdValue ?? undefined,
      }));
      if (!projectScoped)
        localStorage.setItem(
          "lumen:activity-models",
          JSON.stringify(activityModelIds.current),
        );
    },
    [projectScoped],
  );
  const changeActivity = useCallback(
    (next: ComposerActivity) => {
      if (selectedModelId) rememberActivityModel(activity, selectedModelId);
      // Resolve the target mode's model: last used, per-mode default, admin
      // default, then the first compatible model. The conversation model is
      // left untouched so switching modes no longer rewrites every model.
      // When models have not loaded yet the remembered selection is kept and
      // the resolution effect below fills the mode in once they arrive.
      const resolved = preferredActivityModelId(
        models,
        next,
        activityModelIds.current,
        preferences,
      );
      if (resolved) rememberActivityModel(next, resolved);
      setActivity(next);
    },
    [activity, models, preferences, rememberActivityModel, selectedModelId],
  );
  const selectAgent = useCallback(
    (next: string) => {
      setAgentId(next);
      if (!projectScoped) localStorage.setItem("lumen:active-agent", next);
    },
    [projectScoped],
  );
  const changeModel = useCallback(
    (value: string) => {
      rememberActivityModel(activity, value);
      // Only the Ask mode maps to the conversation model; media modes carry
      // their own model on each message via generationParameters.
      if (activity === "ask") void selectModel(value);
    },
    [activity, rememberActivityModel, selectModel],
  );
  const setActivityDefault = useCallback(
    (target: ComposerActivity, modelIdValue: string) => {
      if (!preferences) return;
      const previous = preferences;
      const optimistic: ModelPreferences = {
        ...previous,
        activityDefaultModelIds: {
          ...(previous.activityDefaultModelIds ?? {}),
          [target]: modelIdValue,
        },
        // Ask's default is also the classic global default so new
        // conversations and other surfaces keep working.
        ...(target === "ask" ? { defaultModelId: modelIdValue } : {}),
      };
      setPreferences(optimistic);
      window.dispatchEvent(
        new CustomEvent("lumen:model-preferences", { detail: optimistic }),
      );
      void api<ModelPreferences>("/me/model-preferences", {
        method: "PATCH",
        body: JSON.stringify({
          activityDefaultModelIds: { [target]: modelIdValue },
          ...(target === "ask" ? { defaultModelId: modelIdValue } : {}),
        }),
      })
        .then((saved) => {
          setPreferences(saved);
          window.dispatchEvent(
            new CustomEvent("lumen:model-preferences", { detail: saved }),
          );
        })
        .catch((reason) => {
          setPreferences(previous);
          window.dispatchEvent(
            new CustomEvent("lumen:model-preferences", { detail: previous }),
          );
          setError(
            reason instanceof Error
              ? reason.message
              : "Preference could not be saved",
          );
        });
    },
    [preferences],
  );
  // When a media mode is active without a remembered model (e.g. restored
  // from localStorage or an older conversation) or with a model that is no
  // longer compatible, resolve and remember the mode's default so the header
  // and the message payload agree on it. If no compatible model remains, fall
  // back to Ask (mirroring how the environment resets an unavailable workspace).
  useEffect(() => {
    if (activity === "ask" || !models.length) return;
    if (!models.some((model) => supportsActivity(model, activity))) {
      setActivity("ask");
      return;
    }
    const current = activityModelSelection[activity];
    if (
      current &&
      models.some(
        (model) => model.id === current && supportsActivity(model, activity),
      )
    )
      return;
    const resolved = preferredActivityModelId(
      models,
      activity,
      activityModelIds.current,
      preferences,
    );
    if (resolved) rememberActivityModel(activity, resolved);
  }, [
    activity,
    activityModelSelection,
    models,
    preferences,
    rememberActivityModel,
  ]);
  const {
    send,
    stopGeneration,
    continueResponse,
    continueSubagent,
    answerQuestion,
    decideApproval,
    edit,
    regenerate,
  } = useChatActions({
    activeRequest,
    activityModelIds,
    approvals,
    conversation,
    conversationRef,
    deleteTarget,
    id,
    draftProjectId,
    modelId,
    navigate,
    onAttention,
    onPrepareAttention,
    refreshConversations,
    setActionBusy,
    setApprovals,
    setCompacting,
    setConversation,
    setDecidingApprovalIds,
    setDeleteConversationOpen,
    setDeleteTarget,
    setError,
    setRenameOpen,
    setSending,
    setShareInfo,
    setShareOpen,
    setTitleDraft,
    setUsageRevision,
    sending,
    compacting,
    shareInfo,
    stickToBottom,
    temporary,
    thinkingLevel,
    titleDraft,
    loadApprovals,
    updateConversation,
    applyConversationSnapshot,
    mergeLatestMessages,
    generationMessageId,
  });
  const {
    remove,
    openRename,
    renameConversation,
    openShare,
    enableShare,
    disableShare,
    deleteConversation,
    compactConversation,
  } = useChatConversationActions({
    activeRequest,
    applyConversationSnapshot,
    compacting,
    conversation,
    conversationRef,
    deleteTarget,
    navigate,
    refreshConversations,
    setActionBusy,
    setCompacting,
    setConversation,
    setDeleteConversationOpen,
    setDeleteTarget,
    setError,
    setSending,
    setShareInfo,
    setShareOpen,
    setTitleDraft,
    setRenameOpen,
    shareInfo,
    sending,
    titleDraft,
    updateConversation,
  });
  useChatKeyboardShortcuts({
    conversationRef,
    lastEscapePress,
    regenerate,
    sending,
    stopGeneration,
  });
  const { selectVersion, chooseDeleteTarget } = useChatVersionActions({
    conversationRef,
    setConversation,
    setDeleteTarget,
    setError,
    setSending,
    stickToBottom,
  });
  const shareUrl = shareInfo?.token
    ? `${window.location.origin}/share/${shareInfo.token}`
    : "";
  const latestUserMessage = [...(conversation?.messages ?? [])]
    .reverse()
    .find((message) => message.role === "user");
  const conversationWorkspaceId =
    latestUserMessage?.agentWorkspaceId ??
    (latestUserMessage ? null : undefined);
  const developmentConfigurationLoaded =
    !id || temporary || developmentConfiguration?.conversationId === id;
  const activeDevelopmentConfiguration =
    developmentConfiguration?.conversationId === id
      ? developmentConfiguration
      : null;
  const configuredWorkspaceId =
    conversationWorkspaceId !== undefined
      ? conversationWorkspaceId
      : activeDevelopmentConfiguration
        ? activeDevelopmentConfiguration.workspaceId
        : undefined;
  const handoffRepositoryId =
    (id && localStorage.getItem("lumen:conversation-repository:" + id)) || null;
  const configuredRepositoryId =
    activeDevelopmentConfiguration?.repositoryId ||
    handoffRepositoryId ||
    undefined;
  const panelWorkspaceId =
    latestUserMessage?.agentWorkspaceIds?.[0] ?? configuredWorkspaceId ?? null;
  const panelRepositoryId =
    latestUserMessage?.repositoryIds?.[panelWorkspaceId ?? ""]?.[0] ??
    configuredRepositoryId ??
    null;
  const normalizedProjectDefaults = projectComposerDefaults(
    draftProjectId,
    conversation,
    Boolean(latestUserMessage),
    draftComposerDefaults,
  );
  const composerInitialOptions: Partial<ComposerOptions> | undefined = (() => {
    const messageOptions: Partial<ComposerOptions> | undefined =
      latestUserMessage
        ? {
            toolIds: latestUserMessage.toolIds ?? [],
            artifactIds: latestUserMessage.artifactIds ?? [],
            skillIds: latestUserMessage.skillIds ?? [],
            mcpServerIds: latestUserMessage.mcpServerIds ?? [],
            mcpToolSelections: latestUserMessage.mcpToolSelections ?? {},
            generationParameters: latestUserMessage.generationParameters ?? {},
            agentId: latestUserMessage.agentId ?? null,
            ...(latestUserMessage.agentWorkspaceIds?.length
              ? {
                  agentWorkspaceIds: latestUserMessage.agentWorkspaceIds,
                  repositoryIds: latestUserMessage.repositoryIds ?? {},
                  agentWorkspaceId:
                    latestUserMessage.agentWorkspaceIds.length === 1
                      ? latestUserMessage.agentWorkspaceIds[0]
                      : null,
                  repositoryId: null,
                }
              : latestUserMessage.agentWorkspaceId
                ? {
                    agentWorkspaceId: latestUserMessage.agentWorkspaceId,
                    agentWorkspaceIds: [latestUserMessage.agentWorkspaceId],
                    repositoryIds: latestUserMessage.repositoryIds ?? {},
                    repositoryId:
                      latestUserMessage.repositoryIds?.[
                        latestUserMessage.agentWorkspaceId
                      ]?.[0] ??
                      Object.values(latestUserMessage.repositoryIds ?? {})
                        .flat()
                        .find(Boolean) ??
                      null,
                  }
                : {}),
          }
        : undefined;
    if (messageOptions) {
      if (
        configuredWorkspaceId !== undefined ||
        configuredRepositoryId !== undefined
      ) {
        return {
          ...messageOptions,
          ...(configuredWorkspaceId !== undefined
            ? { agentWorkspaceId: configuredWorkspaceId }
            : {}),
          ...(configuredRepositoryId !== undefined
            ? { repositoryId: configuredRepositoryId }
            : {}),
        };
      }
      return messageOptions;
    }
    if (
      configuredWorkspaceId === undefined &&
      configuredRepositoryId === undefined
    )
      return normalizedProjectDefaults;
    return {
      ...(normalizedProjectDefaults ?? {}),
      ...(configuredWorkspaceId !== undefined
        ? { agentWorkspaceId: configuredWorkspaceId }
        : {}),
      ...(configuredRepositoryId !== undefined
        ? { repositoryId: configuredRepositoryId }
        : {}),
    };
  })();
  const activityModelOptions = useMemo(() => {
    const activities = [
      "image",
      "audio",
      "video",
      "speech",
      "transcription",
      "model3d",
      "sprite",
      "tileset",
    ] as const;
    return Object.fromEntries(
      activities.map((candidate) => [
        candidate,
        models.filter((model) => supportsActivity(model, candidate)),
      ]),
    ) as Record<Exclude<ComposerActivity, "ask">, Model[]>;
  }, [models]);
  // The composer mode picker is independent of activity-tool-section settings.
  // A mode is selectable whenever at least one compatible model exists.
  const availableActivities = useMemo(
    () =>
      (
        [
          "ask",
          "image",
          "audio",
          "video",
          "speech",
          "transcription",
          "model3d",
          "sprite",
          "tileset",
        ] as const
      ).filter(
        (candidate) =>
          candidate === "ask" ||
          (activityModelOptions[candidate]?.length ?? 0) > 0,
      ),
    [activityModelOptions],
  );
  return (
    <ChatPageView
      onToggleSidebar={onToggleSidebar}
      props={{
        activity,
        selectedModelId,
        changeModel,
        activityModels,
        preferences,
        setPreferences,
        selected,
        setActivityDefault,
        temporary,
        conversation,
        setConversation,
        developmentOpen,
        setDevelopmentOpen,
        sending,
        compacting,
        actionBusy,
        openRename,
        openShare,
        setDeleteConversationOpen,
        compactConversation,
        scrollToTop,
        showScrollTop,
        handleScroll,
        handleWheel,
        scrollArea,
        scrollContent,
        loadingConversation,
        loadingOlder,
        modelLookup,
        models,
        configuredRepositoryId,
        agentLookup,
        agents,
        user,
        activityModelOptions,
        decidingApprovalIds,
        approvalsById,
        decideApproval,
        answerQuestion,
        edit,
        regenerate,
        continueResponse,
        continueSubagent,
        chooseDeleteTarget,
        selectVersion,
        error,
        reconnectGeneration,
        todoList,
        send,
        stopGeneration,
        setDevelopmentConfigured,
        agentId,
        selectAgent,
        composerInitialOptions,
        developmentConfigurationLoaded,
        changeActivity,
        availableActivities,
        activityModelSelection,
        activitySettings,
        enabledActivities,
        activityModelIds,
        setActivityModelSelection,
        activitySettingsRef,
        setActivitySettings,
        setEnabledActivities,
        setActivity,
        thinkingLevel,
        setThinkingLevel,
        deleteTarget,
        setDeleteTarget,
        remove,
        renameOpen,
        setRenameOpen,
        titleDraft,
        setTitleDraft,
        renameConversation,
        shareOpen,
        setShareOpen,
        shareInfo,
        shareUrl,
        enableShare,
        disableShare,
        deleteConversationOpen,
        deleteConversation,
        panelWorkspaceId,
        panelRepositoryId,
        developmentConfigured,
        usageRevision,
      }}
    />
  );
}
